import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Stack, Typography } from '@mui/material';
import { ShopperEvent } from '@vs-online-only-app/state';
import ShopperEventsCarousel from './ShopperEventsCarousel';
import { ShopperEventsListItem } from './ShopperEventsListItem';

const PlaceholderWrapper = styled.div<{ $large?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.palette.background.default};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding-top: ${({ $large, theme }) => theme.spacing($large ? 10 : 2)};
  padding-bottom: ${({ $large, theme }) => theme.spacing($large ? 10 : 2)};
`;

type Props = {
  events: ShopperEvent[];
  placeholder: string;
  headerTitle?: string;
  headerIcon?: FC;
  large?: boolean;
  displayAsCarousel?: boolean;
  thumbnailOnly?: boolean;
};

const ShopperEventsList: FC<Props> = ({
  events,
  placeholder,
  headerTitle,
  headerIcon: HeaderIcon,
  large,
  displayAsCarousel,
  thumbnailOnly,
}) => {
  const renderedEvents = useMemo(() => {
    if (!events.length) {
      return (
        <PlaceholderWrapper $large={large}>
          <Typography
            variant={large ? 'body1' : 'body2'}
            fontWeight={large ? 'bold' : 'normal'}
            color="textSecondary"
          >
            {placeholder}
          </Typography>
        </PlaceholderWrapper>
      );
    }

    if (displayAsCarousel) {
      return <ShopperEventsCarousel events={events} />;
    }

    return (
      <Stack direction="row" gap={3} flexWrap="wrap">
        {events.map((event) => (
          <ShopperEventsListItem
            event={event}
            large={large}
            key={event.time}
            thumbnailOnly={thumbnailOnly}
          />
        ))}
      </Stack>
    );
  }, [events, displayAsCarousel, large, placeholder, thumbnailOnly]);

  return (
    <Stack gap={1}>
      {(HeaderIcon || headerTitle) && (
        <Stack direction="row" gap={1} alignItems="center">
          {HeaderIcon && <HeaderIcon />}
          {headerTitle && (
            <Typography
              variant={large ? 'body1' : 'body2'}
              fontWeight={large ? 'bold' : 'normal'}
            >
              {headerTitle}
            </Typography>
          )}
        </Stack>
      )}
      {renderedEvents}
    </Stack>
  );
};

export default ShopperEventsList;
